import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import { AlertTriangle, CheckCircle2, Database, Filter, Search, Tags, Wand2 } from 'lucide-react';

type TagStatus = 'active' | 'ignored' | 'needs_cleanup';

type MetricKey =
  | 'breakouts'
  | 'pores'
  | 'pigmentation'
  | 'redness'
  | 'texture'
  | 'wrinkles'
  | 'hydration'
  | 'oiliness'
  | 'firmness'
  | 'dark_circles';

interface ProductTagGroup {
  id: string;
  sourceField: string;
  rawTags: string[];
  productCount: number;
  canonicalGroup: string;
  mappedMetric: MetricKey | '';
  recommendationNeed: string;
  status: TagStatus;
  cleanupIssue?: string;
  suggestedReplacement?: string;
  sampleProducts: string[];
}

interface SourceCoverage {
  sourceField: string;
  covered: number;
  total: number;
  note: string;
}

const metricOptions: Array<{ key: MetricKey; label: string }> = [
  { key: 'breakouts', label: 'Breakouts' },
  { key: 'pores', label: 'Pores' },
  { key: 'pigmentation', label: 'Pigmentation' },
  { key: 'redness', label: 'Redness' },
  { key: 'texture', label: 'Texture' },
  { key: 'wrinkles', label: 'Wrinkles' },
  { key: 'hydration', label: 'Hydration' },
  { key: 'oiliness', label: 'Oiliness' },
  { key: 'firmness', label: 'Firmness' },
  { key: 'dark_circles', label: 'Dark circles' },
];

const sourceCoverage: SourceCoverage[] = [
  {
    sourceField: 'skin_type_tags',
    covered: 25422,
    total: 26182,
    note: 'Good enough for P1 personalization filters.',
  },
  {
    sourceField: 'skin_concern_tags',
    covered: 25038,
    total: 26182,
    note: 'Primary input for metric and concern matching.',
  },
  {
    sourceField: 'key_ingredients',
    covered: 25392,
    total: 26182,
    note: 'Useful for explanation copy and routine fit.',
  },
  {
    sourceField: 'key_ingredient_tags',
    covered: 22904,
    total: 26182,
    note: 'Lowest coverage among core tag fields.',
  },
  {
    sourceField: 'efficacy_tags',
    covered: 25247,
    total: 26182,
    note: 'Good signal for product benefit matching.',
  },
  {
    sourceField: 'categories',
    covered: 24669,
    total: 26182,
    note: 'Use as fallback and routine category alignment.',
  },
];

const initialTagGroups: ProductTagGroup[] = [
  {
    id: 'pores-synonym',
    sourceField: 'skin_concern_tags',
    rawTags: ['Pore', 'Pores', 'Pore Care'],
    productCount: 1482,
    canonicalGroup: 'pore_care',
    mappedMetric: 'pores',
    recommendationNeed: 'Visible pore care, sebum balancing, lightweight texture',
    status: 'active',
    sampleProducts: ['VT Reedle Shot 100', 'Anua Heartleaf 77 Toner', 'COSRX Two in One Poreless Power Liquid'],
  },
  {
    id: 'dark-circle-synonym',
    sourceField: 'skin_concern_tags',
    rawTags: ['Dark Circle', 'Dark Circles', 'Eye Dullness'],
    productCount: 326,
    canonicalGroup: 'dark_circle_care',
    mappedMetric: 'dark_circles',
    recommendationNeed: 'Eye fatigue support, brightening, mild retinol alternatives',
    status: 'active',
    sampleProducts: ['Beauty of Joseon Revive Eye Serum', 'AXIS-Y Vegan Collagen Eye Serum'],
  },
  {
    id: 'redness-typo',
    sourceField: 'efficacy_tags',
    rawTags: ['Redness Redction'],
    productCount: 42,
    canonicalGroup: 'redness_reduction',
    mappedMetric: 'redness',
    recommendationNeed: 'Calming care for visible redness and irritation-prone skin',
    status: 'needs_cleanup',
    cleanupIssue: 'Typo',
    suggestedReplacement: 'Redness Reduction',
    sampleProducts: ['Dr.G Red Blemish Clear Soothing Cream', 'Skin1004 Madagascar Centella Ampoule'],
  },
  {
    id: 'dryness-typo',
    sourceField: 'skin_concern_tags',
    rawTags: ['Dyrness'],
    productCount: 59,
    canonicalGroup: 'dryness',
    mappedMetric: 'hydration',
    recommendationNeed: 'Barrier hydration, humectants, low-irritation moisturizers',
    status: 'needs_cleanup',
    cleanupIssue: 'Typo',
    suggestedReplacement: 'Dryness',
    sampleProducts: ['Round Lab Birch Juice Moisturizing Cream', 'Torriden Dive-In Serum'],
  },
  {
    id: 'sensitivity-typo',
    sourceField: 'skin_type_tags',
    rawTags: ['Senstivity'],
    productCount: 23,
    canonicalGroup: 'sensitive_skin',
    mappedMetric: 'redness',
    recommendationNeed: 'Low-irritation, fragrance-free, barrier-friendly formulas',
    status: 'needs_cleanup',
    cleanupIssue: 'Typo',
    suggestedReplacement: 'Sensitivity',
    sampleProducts: ['Etude SoonJung 2x Barrier Intensive Cream'],
  },
  {
    id: 'invalid-na',
    sourceField: 'key_ingredient_tags',
    rawTags: ['N/A', '#N/A', 'None', 'Not specified'],
    productCount: 511,
    canonicalGroup: '',
    mappedMetric: '',
    recommendationNeed: '',
    status: 'ignored',
    cleanupIssue: 'Placeholder',
    suggestedReplacement: '',
    sampleProducts: ['Multiple imported products'],
  },
  {
    id: 'jwt-error',
    sourceField: 'key_ingredient_tags',
    rawTags: ['#ERROR: jwt expired'],
    productCount: 7,
    canonicalGroup: '',
    mappedMetric: '',
    recommendationNeed: '',
    status: 'needs_cleanup',
    cleanupIssue: 'Crawler error',
    suggestedReplacement: '',
    sampleProducts: ['Crawler-imported rows'],
  },
  {
    id: 'wrinkle-synonym',
    sourceField: 'efficacy_tags',
    rawTags: ['Wrinkle', 'Wrinkles', 'Anti-aging', 'Firming'],
    productCount: 2134,
    canonicalGroup: 'aging_care',
    mappedMetric: 'wrinkles',
    recommendationNeed: 'Elasticity, fine line care, retinoid or peptide routine fit',
    status: 'active',
    sampleProducts: ['Medicube Deep Vita C Pad', 'Mizon Collagen Power Firming Eye Cream'],
  },
];

const sourceOptions = Array.from(new Set(initialTagGroups.map((group) => group.sourceField)));
const canonicalGroupOptions = [
  '',
  'aging_care',
  'barrier_repair',
  'dark_circle_care',
  'dryness',
  'pore_care',
  'redness_reduction',
  'sensitive_skin',
  'tone_brightening',
];

const statusLabels: Record<TagStatus, { label: string; variant: string }> = {
  active: { label: 'Active mapping', variant: 'success' },
  ignored: { label: 'Ignored', variant: 'secondary' },
  needs_cleanup: { label: 'Needs cleanup', variant: 'warning' },
};

const numberFormatter = new Intl.NumberFormat('en-US');

const formatCount = (value: number): string => numberFormatter.format(value);

const formatCoverage = (item: SourceCoverage): number => Math.round((item.covered / item.total) * 1000) / 10;

export default function ProductTagMappingPage() {
  const [tagGroups, setTagGroups] = useState<ProductTagGroup[]>(initialTagGroups);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<TagStatus | 'all'>('all');
  const [metricFilter, setMetricFilter] = useState<MetricKey | 'all'>('all');
  const [message, setMessage] = useState<string | null>(null);

  const filteredTagGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return tagGroups.filter((group) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        group.rawTags.some((tag) => tag.toLowerCase().includes(normalizedQuery)) ||
        group.canonicalGroup.toLowerCase().includes(normalizedQuery) ||
        group.recommendationNeed.toLowerCase().includes(normalizedQuery);
      const matchesSource = sourceFilter === 'all' || group.sourceField === sourceFilter;
      const matchesStatus = statusFilter === 'all' || group.status === statusFilter;
      const matchesMetric = metricFilter === 'all' || group.mappedMetric === metricFilter;
      return matchesQuery && matchesSource && matchesStatus && matchesMetric;
    });
  }, [metricFilter, query, sourceFilter, statusFilter, tagGroups]);

  const cleanupGroups = useMemo(
    () => tagGroups.filter((group) => group.status === 'needs_cleanup' || Boolean(group.cleanupIssue)),
    [tagGroups],
  );

  const activeMappedCount = tagGroups.filter((group) => group.status === 'active').length;
  const activeMappedProducts = tagGroups
    .filter((group) => group.status === 'active')
    .reduce((sum, group) => sum + group.productCount, 0);
  const cleanupCandidateCount = cleanupGroups.length;
  const invalidProductCount = cleanupGroups.reduce((sum, group) => sum + group.productCount, 0);

  const updateTagGroup = (groupId: string, patch: Partial<ProductTagGroup>) => {
    setTagGroups((current) => current.map((group) => (group.id === groupId ? { ...group, ...patch } : group)));
  };

  const saveDraft = () => {
    setMessage('Draft mapping saved locally for review. API persistence will be connected in SSD-5060 backend work.');
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Badge bg="dark">Skin Analysis P1</Badge>
            <Badge bg="light" text="dark" className="border">
              Mock data
            </Badge>
          </div>
          <h1 className="h3 mb-1">Product Tag Mapping</h1>
          <p className="text-muted mb-0">
            Group raw catalog tags, map them to Skin Analysis metrics, and separate cleanup work from one-off product
            edits.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-dark" onClick={() => setTagGroups(initialTagGroups)}>
            Reset
          </Button>
          <Button variant="dark" onClick={saveDraft}>
            Save draft
          </Button>
        </div>
      </div>

      {message && (
        <Alert variant="success" dismissible onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Published products</div>
                  <div className="h4 mb-0">{formatCount(26182)}</div>
                </div>
                <Database size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Active mappings</div>
                  <div className="h4 mb-0">{activeMappedCount}</div>
                  <div className="text-muted small">{formatCount(activeMappedProducts)} products covered</div>
                </div>
                <CheckCircle2 size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Cleanup groups</div>
                  <div className="h4 mb-0">{cleanupCandidateCount}</div>
                  <div className="text-muted small">{formatCount(invalidProductCount)} affected products</div>
                </div>
                <AlertTriangle size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Core fields</div>
                  <div className="h4 mb-0">6</div>
                  <div className="text-muted small">group-by sources</div>
                </div>
                <Tags size={22} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={5}>
              <Form.Label className="small fw-semibold">Search tag groups</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search raw tag, canonical group, or recommendation need"
                />
              </InputGroup>
            </Col>
            <Col md={4} lg={2}>
              <Form.Label className="small fw-semibold">Source field</Form.Label>
              <Form.Select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
                <option value="all">All sources</option>
                {sourceOptions.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} lg={2}>
              <Form.Label className="small fw-semibold">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as TagStatus | 'all')}
              >
                <option value="all">All statuses</option>
                <option value="active">Active mapping</option>
                <option value="needs_cleanup">Needs cleanup</option>
                <option value="ignored">Ignored</option>
              </Form.Select>
            </Col>
            <Col md={4} lg={3}>
              <Form.Label className="small fw-semibold">Skin metric</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Filter size={16} />
                </InputGroup.Text>
                <Form.Select
                  value={metricFilter}
                  onChange={(event) => setMetricFilter(event.target.value as MetricKey | 'all')}
                >
                  <option value="all">All metrics</option>
                  {metricOptions.map((metric) => (
                    <option key={metric.key} value={metric.key}>
                      {metric.label}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs defaultActiveKey="mapping" className="mb-3">
        <Tab eventKey="mapping" title="Mapping Queue">
          <Card>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '22%' }}>Raw tag group</th>
                      <th style={{ width: '12%' }}>Source</th>
                      <th style={{ width: '9%' }}>Products</th>
                      <th style={{ width: '16%' }}>Canonical group</th>
                      <th style={{ width: '14%' }}>Skin metric</th>
                      <th>Recommendation need</th>
                      <th style={{ width: '12%' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTagGroups.map((group) => {
                      const status = statusLabels[group.status];
                      return (
                        <tr key={group.id}>
                          <td>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              {group.rawTags.map((tag) => (
                                <Badge key={tag} bg="light" text="dark" className="border">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                            <div className="small text-muted">{group.sampleProducts.slice(0, 2).join(', ')}</div>
                          </td>
                          <td>
                            <code className="small">{group.sourceField}</code>
                          </td>
                          <td className="fw-semibold">{formatCount(group.productCount)}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              aria-label={`${group.id} canonical group`}
                              value={group.canonicalGroup}
                              onChange={(event) => updateTagGroup(group.id, { canonicalGroup: event.target.value })}
                            >
                              {canonicalGroupOptions.map((option) => (
                                <option key={option || 'empty'} value={option}>
                                  {option || 'None'}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              aria-label={`${group.id} skin metric`}
                              value={group.mappedMetric}
                              onChange={(event) =>
                                updateTagGroup(group.id, { mappedMetric: event.target.value as MetricKey | '' })
                              }
                            >
                              <option value="">None</option>
                              {metricOptions.map((metric) => (
                                <option key={metric.key} value={metric.key}>
                                  {metric.label}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              size="sm"
                              aria-label={`${group.id} recommendation need`}
                              value={group.recommendationNeed}
                              onChange={(event) => updateTagGroup(group.id, { recommendationNeed: event.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              aria-label={`${group.id} status`}
                              value={group.status}
                              onChange={(event) =>
                                updateTagGroup(group.id, { status: event.target.value as TagStatus })
                              }
                            >
                              <option value="active">Active mapping</option>
                              <option value="needs_cleanup">Needs cleanup</option>
                              <option value="ignored">Ignored</option>
                            </Form.Select>
                            <Badge
                              bg={status.variant}
                              className="mt-2"
                              {...(group.status === 'needs_cleanup' ? { text: 'dark' as const } : {})}
                            >
                              {status.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="cleanup" title="Cleanup Queue">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Wand2 size={18} />
                <div>
                  <h2 className="h6 mb-0">Raw tag cleanup proposals</h2>
                  <div className="text-muted small">
                    These are grouped values to review before any source tag mutation.
                  </div>
                </div>
              </div>
              <Table responsive hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Raw value</th>
                    <th>Issue</th>
                    <th>Suggested replacement</th>
                    <th>Products</th>
                    <th>Current action</th>
                    <th style={{ width: '220px' }}>Review action</th>
                  </tr>
                </thead>
                <tbody>
                  {cleanupGroups.map((group) => (
                    <tr key={group.id}>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {group.rawTags.map((tag) => (
                            <Badge key={tag} bg="light" text="dark" className="border">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td>{group.cleanupIssue ?? 'Review'}</td>
                      <td>{group.suggestedReplacement || <span className="text-muted">No replacement</span>}</td>
                      <td>{formatCount(group.productCount)}</td>
                      <td>
                        <Badge
                          bg={statusLabels[group.status].variant}
                          {...(group.status === 'needs_cleanup' ? { text: 'dark' as const } : {})}
                        >
                          {statusLabels[group.status].label}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() =>
                              updateTagGroup(group.id, {
                                status: 'active',
                                canonicalGroup: group.suggestedReplacement
                                  ? group.suggestedReplacement.toLowerCase().replace(/\s+/g, '_')
                                  : group.canonicalGroup,
                              })
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => updateTagGroup(group.id, { status: 'ignored' })}
                          >
                            Ignore
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="coverage" title="Metric Coverage">
          <Row className="g-3">
            {sourceCoverage.map((item) => {
              const coverage = formatCoverage(item);
              return (
                <Col md={6} xl={4} key={item.sourceField}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between gap-3 mb-2">
                        <div>
                          <code>{item.sourceField}</code>
                          <div className="text-muted small">{item.note}</div>
                        </div>
                        <div className="fw-semibold">{coverage}%</div>
                      </div>
                      <ProgressBar now={coverage} variant={coverage >= 95 ? 'success' : 'warning'} />
                      <div className="small text-muted mt-2">
                        {formatCount(item.covered)} / {formatCount(item.total)} products
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Tab>
      </Tabs>
    </Container>
  );
}
